// TODO: Properly type this file: https://github.com/webflow/webflow/issues/26407

import {
  getCommerceOrderUserItemsCollection,
  getProductCollectionId,
  getSkuCollectionId,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  getItemRefSlug,
  getValueFieldSlug,
} from '@packages/systems/dynamo/utils/ParamFieldPathUtils';
import _ from 'lodash';
import {CONDITION_INVISIBLE_CLASS} from '@packages/systems/dynamo/constants';
import {getDynamoBindingPaths} from '@packages/systems/dynamo/wfdl';
import {getAccessGroupSlugsFromBind} from '@packages/systems/users/usysWfdl';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {isSymbolRoot} from '@packages/systems/core/utils/JsonDomUtils';
import {parse} from '@packages/utilities/json';
import {isSymbolInst} from '@packages/systems/core/utils/DomTypeUtils';
import {
  type ExpressionPath,
  type Type,
  Types,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import type {Expression} from '@packages/systems/wfdl-ast';
import {isRawSortObj} from '@packages/systems/dynamo/utils/DynSortUtils';
import {isRawFilterObj} from '@packages/systems/dynamo/utils/DynQueryUtils';
import {
  parseCollectionListPropTypeName,
  buildCuratedPropsToCollectionIdMap,
  buildFilterPropsToCollectionIdMap,
  buildSortPropsToCollectionIdMap,
} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';
import {dynQueryFieldsPath} from '@packages/systems/dynamo/utils/DynamoElementUtils';

type UsysFieldUsage = {
  [fieldId: string]: string[];
};
type UsysGroupUsage = {
  [accessGroupShortId: string]: string[];
};

type DynFieldUsage = {
  collections?: {
    [collectionId: string]: {
      [slugId: string]: number;
    };
  };
  items?: {
    [itemId: string]: number;
  };
};

// @ts-expect-error - TS7006 - Parameter 'jsonNode' implicitly has an 'any' type.
function getUsysField(jsonNode) {
  return jsonNode.data?.users?.field ?? null;
}

// @ts-expect-error - TS7006 - Parameter 'jsonNode' implicitly has an 'any' type.
function getAccessGroupSlugs(jsonNode): string[] {
  const bind = jsonNode?.bind;
  return bind ? getAccessGroupSlugsFromBind(bind) : [];
}

// @ts-expect-error - TS7006 - Parameter 'jsonNode' implicitly has an 'any' type.
function getNodeId(jsonNode) {
  return jsonNode._id || '';
}

/* Increment the number of times a field is used in the given usage map */
function increaseDynFieldUsageCount(
  dynFieldUsage: DynFieldUsage,
  collectionId: string | null,
  fieldSlug: string
) {
  if (!collectionId) {
    console.error('Unknown collection for field slug: ' + fieldSlug);
    return;
  }

  // Ignore "_id" because it's not a real field, and it can't be deleted
  // It's used in filters/condvis to compare to DYN_CONTEXT usually
  if (fieldSlug === '_id') {
    return;
  }

  if (dynFieldUsage.collections === undefined) {
    dynFieldUsage.collections = {};
  }

  if (dynFieldUsage.collections[collectionId] === undefined) {
    dynFieldUsage.collections[collectionId] = {};
  }

  // Count the number of times this field is used across all nodes
  const collection = dynFieldUsage.collections[collectionId];
  if (collection[fieldSlug] === undefined) {
    collection[fieldSlug] = 1;
  } else {
    collection[fieldSlug]++;
  }
}

function getTextAtPath(expression: Expression, path: ExpressionPath) {
  const exprAtPath = Expressions.getIn(expression, path);
  return exprAtPath ? Expressions.getText(exprAtPath) : null;
}

// Static filter values stay in `bind` when they share a Distinct with a binding
function getTextOrTextListAtPath(
  expression: Expression,
  path: ExpressionPath
): string | string[] | null {
  const exprAtPath = Expressions.getIn(expression, path);
  if (!exprAtPath) {
    return null;
  }
  const text = Expressions.getText(exprAtPath);
  if (text != null) {
    return text;
  }
  const list = Expressions.getList(exprAtPath);
  return list
    ? list
        .map((item) => Expressions.getText(item))
        .filter((item): item is string => item != null)
    : null;
}

const getDynContextSlugs = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value]).flatMap((item) =>
    typeof item === 'string' && /^DYN_CONTEXT\..+/.test(item)
      ? [item.replace(/^DYN_CONTEXT\./, '')]
      : []
  );

const FILTER_FIELD_PATH: ExpressionPath = [{in: 'Record', at: 'fieldPath'}];
const FILTER_VALUE_PATH: ExpressionPath = [{in: 'Record', at: 'value'}];

function increaseItemUsageCount(
  collectionFieldMap: Record<string, any>,
  itemId: string
) {
  if (!itemId) {
    console.error('Unknown item with an id ', itemId);
    return;
  }

  if (collectionFieldMap.items === undefined) {
    collectionFieldMap.items = {};
  }

  // Count the number of times this item is used in query filters
  const items = collectionFieldMap.items;
  if (items[itemId] === undefined) {
    items[itemId] = 1;
  } else {
    items[itemId]++;
  }
}

/**
 * Utility methods shared by both frontend / backend to generate a
 * siteDependencies.usedDynFieldsMap for pages with dynamic content.
 */

/**
 * Walks through a flat list of JSON DomNodes and returns a map
 * that shows how many times each field is used across bindings in all the nodes.
 */
export const getBoundFieldMaps = function (
  dataTypes: Plugins.DataTypes,
  jsonDomNodes: JSONNode[],
  detailPageCollectionId: null | string,
  collections: any
): {
  dynFieldUsage: DynFieldUsage;
  usysFieldUsage: UsysFieldUsage;
  usysGroupUsage: UsysGroupUsage;
} {
  const collectionFieldMap: Record<string, any> = {};
  const usysFieldUsage: Record<string, any> = {};
  const usysGroupUsage: Record<string, any> = {};
  const parentIndex = buildNodeParentIndex(dataTypes, jsonDomNodes);

  // @ts-expect-error - TS7006 - Parameter 'leafNode' implicitly has an 'any' type.
  const findClosestDynamoWrapper = function (leafNode) {
    return findAncestorNodeBySelector(
      parentIndex,
      leafNode,
      function (parentNode) {
        return (
          parentNode &&
          parentNode.data &&
          parentNode.data.dyn &&
          parentNode.data.dyn.type === 'wrapper'
        );
      }
    );
  };

  // @ts-expect-error - TS7006 - Parameter 'leafNode' implicitly has an 'any' type.
  const findClosestDynamoList = function (leafNode) {
    return findAncestorNodeBySelector(
      parentIndex,
      leafNode,
      function (parentNode) {
        return (
          (parentNode && parentNode.type && parentNode.type === 'DynamoList') ||
          (parentNode &&
            parentNode.data &&
            parentNode.data.dyn &&
            parentNode.data.dyn.type === 'list')
        );
      }
    );
  };

  // @ts-expect-error - TS7006 - Parameter 'leafNode' implicitly has an 'any' type.
  const findClosestCommerceOrderItemsList = function (leafNode) {
    return findAncestorNodeBySelector(
      parentIndex,
      leafNode,
      function (parentNode) {
        return (
          (parentNode &&
            parentNode.data &&
            parentNode.data.commerce &&
            parentNode.data.commerce.type === 'cart-list') ||
          // TODO: add check for the CommerceCheckoutOrderItemsList
          (parentNode &&
            parentNode.type &&
            parentNode.type === 'CommerceCheckoutOrderItemsList')
        );
      }
    );
  };

  // @ts-expect-error - TS7006 - Parameter 'contextCollectionId' implicitly has an 'any' type. | TS7006 - Parameter 'itemRefFieldSlug' implicitly has an 'any' type.
  function getReferencedCollectionId(contextCollectionId, itemRefFieldSlug) {
    const contextCollection = collections.find(
      // @ts-expect-error - TS7006 - Parameter 'coll' implicitly has an 'any' type.
      (coll) => String(coll._id) === contextCollectionId
    );
    const itemRefField = contextCollection
      ? contextCollection.fields.find(
          // @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type.
          (field) => field.slug === itemRefFieldSlug
        )
      : null;
    const referencedCollectionId = itemRefField?.validations?.collectionId;
    return referencedCollectionId;
  }

  function _increaseDynFieldUsageCount(
    collectionId: string | null,
    fieldSlug: string
  ) {
    increaseDynFieldUsageCount(collectionFieldMap, collectionId, fieldSlug);
  }

  function _increaseUsysFieldUsageCount(usysFieldId: any, nodeId: string) {
    if (usysFieldUsage[usysFieldId] === undefined) {
      usysFieldUsage[usysFieldId] = [];
    }
    usysFieldUsage[usysFieldId].push(nodeId);
  }

  function _increaseAccessGroupUsageCount(accessGroup: string, nodeId: string) {
    if (usysGroupUsage[accessGroup] === undefined) {
      usysGroupUsage[accessGroup] = [];
    }
    usysGroupUsage[accessGroup].push(nodeId);
  }

  for (let i = 0, len = jsonDomNodes.length; i < len; i++) {
    const jsonNode = jsonDomNodes[i];

    const nodeId = getNodeId(jsonNode);
    if (nodeId) {
      const usysFieldId = getUsysField(jsonNode);
      if (usysFieldId !== null) {
        _increaseUsysFieldUsageCount(usysFieldId, nodeId);
      }
      const accessGroups = getAccessGroupSlugs(jsonNode);

      for (const accessGroup of accessGroups) {
        _increaseAccessGroupUsageCount(accessGroup, nodeId);
      }
    }

    const sym = jsonNode?.data?.sym;
    const inst = sym?.inst;
    const overrides = sym?.overrides;
    const dataType = inst ? dataTypes.get(['__SitePlugin', inst]) : null;
    // A bound override can live only in `bind`, with no entry in data.sym.overrides
    const bindOverridesExpr = jsonNode?.bind
      ? Expressions.getIn(jsonNode.bind, [
          {in: 'Record', at: 'sym'},
          {in: 'Record', at: 'overrides'},
        ])
      : undefined;
    const bindOverrideIds = bindOverridesExpr
      ? Object.keys(Expressions.getRecord(bindOverridesExpr) ?? {})
      : [];

    // Process symbol instances with overrides that might contain CMS field references
    if (jsonNode && dataType && (overrides || bindOverrideIds.length > 0)) {
      // Build maps to associate filter/sort property IDs with their collection IDs
      // These maps help identify which collection a field belongs to
      const filterPropsToCollectionIdMap =
        buildFilterPropsToCollectionIdMap(dataType);

      const sortPropsToCollectionIdMap =
        buildSortPropsToCollectionIdMap(dataType);

      const curatedPropsToCollectionIdMap =
        buildCuratedPropsToCollectionIdMap(dataType);

      // DYN_CONTEXT resolves against the parent binding context, which starts at
      // the closest DynamoItem (an Empty State keeps its parent's), else the page
      const closestDynamoItem = findAncestorNodeBySelector(
        parentIndex,
        jsonNode,
        (parentNode) =>
          parentNode?.type === 'DynamoItem' ||
          parentNode?.data?.dyn?.type === 'item'
      );
      const dynContextCollectionId = closestDynamoItem
        ? (findClosestDynamoWrapper(closestDynamoItem)?.data?.dyn?.collection
            ?.id ?? null)
        : detailPageCollectionId;

      // Iterate through each override field in the symbol instance
      const overrideFieldIds = new Set([
        ...Object.keys(overrides ?? {}),
        ...bindOverrideIds,
      ]);
      for (const overrideFieldId of overrideFieldIds) {
        const override: unknown = overrides?.[overrideFieldId];

        // Process filter overrides - these contain CMS field references for filtering
        if (overrideFieldId in filterPropsToCollectionIdMap) {
          const collectionId = filterPropsToCollectionIdMap[overrideFieldId];

          // Handle direct filter field references in the override data
          if (
            collectionId &&
            override &&
            typeof override === 'object' &&
            'fields' in override &&
            Array.isArray(override.fields)
          ) {
            // Process each filter field to track CMS field usage
            override.fields.forEach((field: unknown) => {
              if (isRawFilterObj(field)) {
                getDynContextSlugs(field.value).forEach((dynContextSlug) => {
                  _increaseDynFieldUsageCount(
                    dynContextCollectionId,
                    dynContextSlug
                  );
                });
                // Record this field as being used by this node
                _increaseDynFieldUsageCount(collectionId, field.fieldPath);
                // Record any referenced item ids
                const itemIds = extractItemIdsFromValue(field.value);
                itemIds.forEach((itemId) => {
                  increaseItemUsageCount(collectionFieldMap, itemId);
                });
              }
            });
          }

          // Handle filter field references when the filter property has universal binding
          if (collectionId && jsonNode.bind) {
            // Navigate through the expression tree to find filter fields
            const overrideExpr = Expressions.getIn(jsonNode.bind, [
              {in: 'Record', at: 'sym'},
              {in: 'Record', at: 'overrides'},
              {in: 'Record', at: overrideFieldId},
            ]);
            // The designer wraps filter values in a Distinct; older saves store a plain Record
            const overrideFieldsExpr =
              overrideExpr &&
              (Expressions.getIn(overrideExpr, [
                {in: 'Distinct'},
                {in: 'Record', at: 'fields'},
              ]) ??
                Expressions.getIn(overrideExpr, [
                  {in: 'Record', at: 'fields'},
                ]));

            if (overrideFieldsExpr) {
              // Extract the list of filter fields from the expression
              const overrideFields = Expressions.getList(overrideFieldsExpr);
              if (overrideFields) {
                // Process each filter field expression
                overrideFields.forEach((overrideField) => {
                  const fieldPath = getTextAtPath(
                    overrideField,
                    FILTER_FIELD_PATH
                  );
                  if (fieldPath) {
                    // Record this field as being used by this node
                    _increaseDynFieldUsageCount(collectionId, fieldPath);
                  }
                  const value = getTextOrTextListAtPath(
                    overrideField,
                    FILTER_VALUE_PATH
                  );
                  // Record any referenced item ids
                  const itemIds = extractItemIdsFromValue(value);
                  itemIds.forEach((itemId) => {
                    increaseItemUsageCount(collectionFieldMap, itemId);
                  });
                  // DYN_CONTEXT fields belong to the parent context's collection, not the filtered one
                  getDynContextSlugs(value).forEach((dynContextSlug) => {
                    _increaseDynFieldUsageCount(
                      dynContextCollectionId,
                      dynContextSlug
                    );
                  });
                });
              }
            }
          }
        }

        // Process sort overrides - these contain CMS field references for sorting
        if (overrideFieldId in sortPropsToCollectionIdMap) {
          const collectionId = sortPropsToCollectionIdMap[overrideFieldId];
          // Sort overrides are arrays of sort specifications
          if (collectionId && Array.isArray(override)) {
            // Process each sort field to track CMS field usage
            override.forEach((sortField: unknown) => {
              if (
                isRawSortObj(sortField) &&
                sortField.field !== '_random' // Skip special '_random' sort which isn't a real field
              ) {
                // Record this sort field in our dependency tracking
                // This tracks which CMS fields are used for sorting in this collection
                _increaseDynFieldUsageCount(collectionId, sortField.field);
              }
            });
          }
        }

        // Process curated overrides - these contain CMS item references
        if (overrideFieldId in curatedPropsToCollectionIdMap) {
          const collectionId = curatedPropsToCollectionIdMap[overrideFieldId];
          if (
            collectionId &&
            override &&
            typeof override === 'object' &&
            'itemIds' in override &&
            Array.isArray(override.itemIds)
          ) {
            // Process each item id to track CMS item usage
            override.itemIds.forEach((itemId: unknown) => {
              if (typeof itemId === 'string') {
                increaseItemUsageCount(collectionFieldMap, itemId);
              }
            });
          }
        }
      }
    }

    // We only care about dynamically-bound nodes here
    if (
      !jsonNode ||
      !jsonNode.data ||
      (!jsonNode.data.dyn && !jsonNode.data.commerce && !jsonNode.bind)
    ) {
      continue;
    }

    const dynData = jsonNode.data?.dyn || jsonNode.data?.commerce || {};

    // For dynamic lists, read through ItemRefSet pointers and query fields
    // Both of these currently use slugs instead of IDs, so we have to translate them
    if (dynData.type === 'wrapper') {
      const listCollectionId = dynData.collection && dynData.collection.id;
      if (listCollectionId) {
        // Consider DynList bindings to count towards field usage
        _increaseDynFieldUsageCount(listCollectionId, '_collection');

        // If a DynList gets its data from a Multi-Reference field, treat that field as a dependency
        const multiRefFieldSlug =
          Boolean(listCollectionId) && dynData.collection.itemRefSet;
        if (multiRefFieldSlug) {
          _increaseDynFieldUsageCount(
            dynData.collection.contextCollectionId,
            multiRefFieldSlug
          );
        }

        // If the list has any filters or sorts, collect all filters by slug
        const queryData = dynData.query || {};
        if (queryData.filter && queryData.filter.fields) {
          const {fields} = queryData.filter;
          const queryFieldSlugs = Array.isArray(fields)
            ? fields.map((field) => field.fieldPath)
            : _.keys(fields);

          if (queryFieldSlugs && queryFieldSlugs.length) {
            _.each(queryFieldSlugs, function (fieldSlug) {
              _increaseDynFieldUsageCount(listCollectionId, fieldSlug);
            });
          }

          const filterItemIds = parseFilterItemIdsFromFields(fields);
          _.each(filterItemIds, function (itemId) {
            increaseItemUsageCount(collectionFieldMap, itemId);
          });

          // If the list has any filters with `DYN_CONTEXT.fieldSlug` as the value, collect `fieldSlug`
          if (detailPageCollectionId) {
            const iteratee = Array.isArray(fields)
              ? (f: any) => f.value
              : _.values;
            const fieldValues = _.flatMapDeep(fields, iteratee);

            fieldValues
              .filter(function (val) {
                return /^DYN_CONTEXT\..+/.test(val);
              })
              .forEach(function (value) {
                _increaseDynFieldUsageCount(
                  detailPageCollectionId,
                  value.replace(/^DYN_CONTEXT\./, '')
                );
              });
          }
        }

        const sortData = queryData.sort ?? [];

        if (sortData.length) {
          for (const sortField of sortData) {
            if (
              isRawSortObj(sortField) &&
              sortField.field !== '_random' // Skip special '_random' sort which isn't a real field
            ) {
              _increaseDynFieldUsageCount(listCollectionId, sortField.field);
            }
          }
        }
      }
    }

    // search jsonNode.bind for Dynamo universal bindings
    const dynamoUniversalBindingPaths = jsonNode.bind
      ? getDynamoBindingPaths(jsonNode.bind)
      : [];

    // Detect the closest dynamic list context, and fall back to detail page context if no list is found
    let closestDynList = null;
    let currentContextCollectionId = detailPageCollectionId || null;
    if (
      dynData.bind ||
      dynData.condition ||
      dynamoUniversalBindingPaths.length > 0
    ) {
      const closestCommerceOrderItemsList =
        findClosestCommerceOrderItemsList(jsonNode);
      closestDynList = findClosestDynamoWrapper(jsonNode);

      if (closestCommerceOrderItemsList) {
        const productCollectionId = getProductCollectionId(collections);
        const skuCollectionId = getSkuCollectionId(collections);

        const orderItemsCollection =
          productCollectionId &&
          skuCollectionId &&
          getCommerceOrderUserItemsCollection(
            productCollectionId,
            skuCollectionId
          );

        if (orderItemsCollection) {
          currentContextCollectionId = orderItemsCollection._id;
          collections.push(orderItemsCollection);
        }
      } else if (
        closestDynList &&
        closestDynList.data &&
        closestDynList.data.dyn &&
        closestDynList.data.dyn.collection &&
        closestDynList.data.dyn.collection.id
      ) {
        // Once we're within the DynamoList and have reached the DynamoItem, we switch the context to the collection wrapper collection id
        const insideDynamoList = Boolean(findClosestDynamoList(jsonNode));
        if (insideDynamoList) {
          currentContextCollectionId = closestDynList.data.dyn.collection.id;
        }
      }
    }

    // If a node is directly tied to a field value, collect field IDs directly from the binding paths
    const bindData =
      dynData.bind && _.keys(dynData.bind).length > 0 ? dynData.bind : null;

    if (bindData) {
      // Unpack binding paths, going deeper into the inline "style" binding if needed
      const bindingPaths = _.flattenDeep(
        _.map(_.values(bindData), function (value) {
          return _.isArray(value) ? value : _.values(value);
        })
      );

      _.each(bindingPaths, function (pathPart, index) {
        const fieldId = pathPart.id;
        const fieldSlug = pathPart.slug;

        // Ignore binding paths where id === slug, which are subfield keypaths and not actual fields
        if (!fieldId || (fieldId && fieldId === fieldSlug)) {
          return;
        }

        const previousPart = index > 0 ? bindingPaths[index - 1] : null;
        const isAfterItemRef =
          previousPart !== null &&
          Boolean(previousPart) &&
          previousPart.type === 'ItemRef';
        let contextCollectionId = null;

        if (isAfterItemRef) {
          const referencedCollectionId = previousPart
            ? getReferencedCollectionId(
                currentContextCollectionId,
                previousPart.slug
              )
            : null;
          contextCollectionId = referencedCollectionId || null;
        } else {
          contextCollectionId = currentContextCollectionId;
        }

        // Increase the number of times this field is used on the current page
        if (contextCollectionId) {
          _increaseDynFieldUsageCount(contextCollectionId, fieldSlug);
        }
      });
    }

    // Detect slugs used inside conditional visibility data
    const condition = dynData.condition || null;
    const conditionFields =
      condition &&
      condition.class &&
      condition.class.false &&
      condition.class.false[CONDITION_INVISIBLE_CLASS] &&
      condition.class.false[CONDITION_INVISIBLE_CLASS].fields;
    const conditionFieldSlugPaths =
      conditionFields && _.isObject(conditionFields)
        ? _.keys(conditionFields)
        : [];
    if (conditionFieldSlugPaths.length) {
      _.each(conditionFieldSlugPaths, (fieldSlugPath) => {
        const itemRefFieldSlug = getItemRefSlug(fieldSlugPath);
        if (itemRefFieldSlug) {
          const referencedCollectionId = getReferencedCollectionId(
            currentContextCollectionId,
            itemRefFieldSlug
          );
          if (referencedCollectionId) {
            _increaseDynFieldUsageCount(
              currentContextCollectionId,
              itemRefFieldSlug
            );
            _increaseDynFieldUsageCount(
              referencedCollectionId,
              getValueFieldSlug(fieldSlugPath)
            );
          }
        } else {
          _increaseDynFieldUsageCount(
            currentContextCollectionId,
            getValueFieldSlug(fieldSlugPath)
          );
        }
      });

      if (detailPageCollectionId) {
        _.flatMapDeep(conditionFields || {}, _.values).forEach((value) => {
          if (/^DYN_CONTEXT\..+/.test(value)) {
            _increaseDynFieldUsageCount(
              detailPageCollectionId,
              value.replace('DYN_CONTEXT.', '')
            );
          }
        });
      }
    }

    if (dynamoUniversalBindingPaths.length > 0) {
      dynamoUniversalBindingPaths.forEach((bindingPath) => {
        if (bindingPath.length === 1) {
          const valueFieldSlug = bindingPath[0];
          if (valueFieldSlug && valueFieldSlug !== 'DYN_CONTEXT') {
            _increaseDynFieldUsageCount(
              currentContextCollectionId,
              valueFieldSlug
            );
          }
        } else if (bindingPath.length === 2) {
          const [itemRefFieldSlug, valueFieldSlug] = bindingPath;

          if (itemRefFieldSlug === 'DYN_CONTEXT') {
            if (detailPageCollectionId && valueFieldSlug) {
              _increaseDynFieldUsageCount(
                detailPageCollectionId,
                valueFieldSlug
              );
            }
          } else {
            const referencedCollectionId = getReferencedCollectionId(
              currentContextCollectionId,
              itemRefFieldSlug
            );

            if (referencedCollectionId && itemRefFieldSlug && valueFieldSlug) {
              _increaseDynFieldUsageCount(
                currentContextCollectionId,
                itemRefFieldSlug
              );
              _increaseDynFieldUsageCount(
                referencedCollectionId,
                valueFieldSlug
              );
            }
          }
        }
      });
    }
    const nodeBindData = jsonNode.bind;
    if (nodeBindData) {
      const filterFieldsExpr = Expressions.getIn(
        nodeBindData,
        dynQueryFieldsPath
      );
      if (filterFieldsExpr) {
        const filterFields = Expressions.getList(filterFieldsExpr);
        if (filterFields) {
          filterFields.forEach((filterField) => {
            const fieldPath = getTextAtPath(filterField, FILTER_FIELD_PATH);
            if (fieldPath) {
              _increaseDynFieldUsageCount(
                currentContextCollectionId,
                fieldPath
              );
            }
            const value = getTextAtPath(filterField, FILTER_VALUE_PATH);
            const itemIds = extractItemIdsFromValue(value);
            itemIds.forEach((itemId) => {
              increaseItemUsageCount(collectionFieldMap, itemId);
            });
            if (
              detailPageCollectionId &&
              typeof value === 'string' &&
              /^DYN_CONTEXT\..+/.test(value)
            ) {
              _increaseDynFieldUsageCount(
                currentContextCollectionId,
                value.replace(/^DYN_CONTEXT\./, '')
              );
            }
          });
        }
      }
    }
  }

  const dynFieldUsage: DynFieldUsage = {};
  // Make the map key order deterministic by sorting the keys alphabetically
  const sortedKeys = _.keys(collectionFieldMap).sort();
  _.each(sortedKeys, function (key) {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DynFieldUsage'.
    dynFieldUsage[key] = collectionFieldMap[key];
  });

  return {dynFieldUsage, usysFieldUsage, usysGroupUsage};
};

/**
 * Walks through a flat list of JSON DomNodes that make up a site's symbols and returns a map
 * that shows how many times each field is used across bindings in each symbol.
 * This method assumes that symbol root nodes are the symbol delimiters.
 */
export const getBoundDynFieldSymbolMap = function (
  dataTypes: Plugins.DataTypes,
  jsonDomNodes: JSONNode[],
  collections: any
): {
  dynFieldsUsedInSymbols: {
    [nodeId: string]: DynFieldUsage;
  };
  usysGroupsUsedInSymbols: {
    [symbolId: string]: UsysGroupUsage;
  };
} {
  const usysGroupsUsedInSymbols: Record<string, any> = {};
  const dynFieldsUsedInSymbols: Record<string, any> = {};
  function _addSymbolToUsageMap(symbolNodes: Array<JSONNode>) {
    const firstNode = symbolNodes.length ? symbolNodes[0] : null;

    if (firstNode && isSymbolRoot(firstNode)) {
      const {dynFieldUsage, usysGroupUsage} = getBoundFieldMaps(
        dataTypes,
        symbolNodes,
        null,
        collections
      );

      const usedCollectionIds =
        getUsedCollectionIdsFromComponentDataType(firstNode);
      for (const collectionId of usedCollectionIds) {
        increaseDynFieldUsageCount(dynFieldUsage, collectionId, '_collection');
      }

      if (firstNode.dataType) {
        // Detecting the CMS field usages in Filter and Sort prop default values
        const dataType =
          typeof firstNode.dataType === 'string'
            ? (parse(firstNode.dataType) as Type)
            : firstNode.dataType;

        // Create a mapping of filter property IDs to their associated collection IDs
        const filterPropsToCollectionIdMap =
          buildFilterPropsToCollectionIdMap(dataType);

        // Create a mapping of sort property IDs to their associated collection IDs
        const sortPropsToCollectionIdMap =
          buildSortPropsToCollectionIdMap(dataType);

        // Create a mapping of curated property IDs to their associated collection IDs
        const curatedPropsToCollectionIdMap =
          buildCuratedPropsToCollectionIdMap(dataType);

        // Extract all fields from the component's data type
        const fields = Types.getRecordFields(dataType);

        if (fields) {
          // Iterate through each field in the component's data type
          for (const fieldId in fields) {
            const fieldType = fields[fieldId];
            if (!fieldType) continue;

            // Get the constructor name of the field type
            const typeName = Types.getTypeConstructor(fieldType);
            if (!typeName) continue;

            // Process filter properties - these are used to filter CMS collection items
            if (fieldId in filterPropsToCollectionIdMap) {
              const collectionId = filterPropsToCollectionIdMap[fieldId];
              if (!collectionId) continue;

              // Get the default value for this filter property
              const defaultValue: unknown = Types.getDefaultValue(
                fieldType,
                null
              );

              // Check if the default value is a filter object with fields array
              if (
                defaultValue &&
                typeof defaultValue === 'object' &&
                'fields' in defaultValue &&
                Array.isArray(defaultValue.fields)
              ) {
                // Process each filter in the fields array
                defaultValue.fields.forEach((filter: unknown) => {
                  // Verify the filter has a fieldPath property
                  if (isRawFilterObj(filter)) {
                    // Record this field usage in our dependency tracking
                    increaseDynFieldUsageCount(
                      dynFieldUsage,
                      collectionId,
                      filter.fieldPath
                    );
                    // Record any referenced item ids
                    const itemIds = extractItemIdsFromValue(filter.value);
                    itemIds.forEach((itemId) => {
                      increaseItemUsageCount(dynFieldUsage, itemId);
                    });
                  }
                });
              }
            }

            // Process sort properties - these are used to sort CMS collection items
            if (fieldId in sortPropsToCollectionIdMap) {
              const collectionId = sortPropsToCollectionIdMap[fieldId];
              if (!collectionId) continue;

              // Get the default value for this sort property
              const defaultValue: unknown = Types.getDefaultValue(
                fieldType,
                null
              );

              // Check if the default value is an array of sort fields
              if (Array.isArray(defaultValue)) {
                // Process each sort field in the array
                defaultValue.forEach((sortField: unknown) => {
                  // Verify the sort field has a valid field property
                  // (excluding '_random' which is a special case)
                  if (
                    isRawSortObj(sortField) &&
                    sortField.field !== '_random'
                  ) {
                    // Record this field usage in our dependency tracking
                    increaseDynFieldUsageCount(
                      dynFieldUsage,
                      collectionId,
                      sortField.field
                    );
                  }
                });
              }
            }

            // Process curated properties - these are used to manually select CMS items
            if (fieldId in curatedPropsToCollectionIdMap) {
              const collectionId = curatedPropsToCollectionIdMap[fieldId];
              if (!collectionId) continue;

              // Get the default value for this curated property
              const defaultValue: unknown = Types.getDefaultValue(
                fieldType,
                null
              );

              // Check if the default value is a curated object with itemIds array
              if (
                defaultValue &&
                typeof defaultValue === 'object' &&
                'itemIds' in defaultValue &&
                Array.isArray(defaultValue.itemIds)
              ) {
                const itemIds = extractItemIdsFromValue(defaultValue.itemIds);

                itemIds.forEach((itemId) => {
                  increaseItemUsageCount(dynFieldUsage, itemId);
                });
              }
            }
          }
        }
      }

      if (dynFieldUsage && _.keys(dynFieldUsage).length > 0) {
        dynFieldsUsedInSymbols[firstNode._id] = dynFieldUsage;
      }
      if (usysGroupUsage && _.keys(usysGroupUsage).length > 0) {
        usysGroupsUsedInSymbols[firstNode._id] = usysGroupUsage;
      }
    }
  }

  let currentSymbolNodes: Array<JSONNode> = [];

  // Make a pass through all the flattened symbol nodes, forming maps as we pass root symbol boundaries
  for (let i = 0, len = jsonDomNodes.length; i < len; i++) {
    // @ts-expect-error - TS2345 - Argument of type 'JSONNode | undefined' is not assignable to parameter of type 'JSONNode'.
    currentSymbolNodes.push(jsonDomNodes[i]);

    const nextNode = jsonDomNodes[i + 1];
    const lastNodeInSymbol = i + 1 === len || nextNode?.data?.sym?.root;
    if (lastNodeInSymbol) {
      _addSymbolToUsageMap(currentSymbolNodes);

      // Reset to start collecting nodes for the next symbol
      currentSymbolNodes = [];
    }
  }

  return {dynFieldsUsedInSymbols, usysGroupsUsedInSymbols};
};

/**
 * Given a filter value, extract any mongo ids from it.
 */
function extractItemIdsFromValue(value: unknown): string[] {
  const mongoIdTester = /^[0-9a-f]{24}$/;

  if (_.isArray(value)) {
    return _.filter(value, function (s) {
      return _.isString(s) && s.match(mongoIdTester);
    });
  } else if (_.isString(value) && value.match(mongoIdTester)) {
    return [value];
  }

  return [];
}

/**
 * Given a list of filter fields, extract any mongo ids from the values.
 */
function parseFilterItemIdsFromFields(fields: unknown): string[] {
  if (!Array.isArray(fields)) return [];

  return _.reduce(
    fields,
    function (ids: string[], filter) {
      const filters = _.values(filter);
      const filterItems = _.reduce(
        filters,
        function (filterIds: string[], value) {
          return filterIds.concat(extractItemIdsFromValue(value));
        },
        []
      );
      return ids.concat(filterItems);
    },
    []
  );
}

/**
 * Extract collection ids referenced in the dataType of a component root node
 *
 * Given a JSON node representing the component root, find any collections
 * referenced in the component dataType and return their ids as an array.
 *
 * Note! This method assumes the caller has already validated that the node
 * represents a component root.
 *
 * @param rootNode - a JSON node representing a component root
 */
function getUsedCollectionIdsFromComponentDataType(
  rootNode: JSONNode
): string[] {
  const rawDataType = rootNode.dataType;
  if (rawDataType) {
    const dataType =
      typeof rawDataType === 'string'
        ? (parse(rawDataType) as undefined | Type)
        : rawDataType;

    const fields = dataType ? Types.getRecordFields(dataType) : null;
    if (fields) {
      return Object.values(fields).reduce((collectionIds: string[], type) => {
        const name = Types.getTypeConstructor(type);
        if (name) {
          const parsed = parseCollectionListPropTypeName(name);
          if (parsed) {
            collectionIds.push(parsed.collectionId);
          }
        }
        return collectionIds;
      }, []);
    }
  }
  return [];
}

/**
 * Returns an array of slot override IDs for a symbol instance node.
 * If the node is not a symbol instance, returns an empty array.
 */
export function getSlotOverrideIdsFromInstance(
  dataTypes: Plugins.DataTypes,
  jsonNode: JSONNode
): string[] {
  const inst = jsonNode.data?.sym?.inst;

  if (!inst) {
    return [];
  }

  const dataType = dataTypes.get(['__SitePlugin', inst]);
  const overrideFieldsTypes = dataType ? Types.getRecordFields(dataType) : null;

  if (!overrideFieldsTypes) {
    return [];
  }

  return Object.keys(overrideFieldsTypes).filter((overrideKey) => {
    const overrideFieldType = overrideFieldsTypes[overrideKey];
    return overrideFieldType && Types.isSlotContentType(overrideFieldType);
  });
}

/**
 * Returns a map that can be used to look up a node's parent node.
 */
export const buildNodeParentIndex = function (
  dataTypes: Plugins.DataTypes,
  jsonDomNodes: Array<JSONNode>
) {
  const parentMap: Record<string, JSONNode> = {};

  _.each(jsonDomNodes, function (parentNode) {
    if (isSymbolInst(parentNode.data)) {
      const slotOverrideIds = getSlotOverrideIdsFromInstance(
        dataTypes,
        parentNode
      );
      for (const slotOverrideId of slotOverrideIds) {
        const slotChildren = parentNode.data?.sym.overrides?.[slotOverrideId];

        if (slotChildren && Array.isArray(slotChildren)) {
          slotChildren.forEach((slotChildId: unknown) => {
            if (typeof slotChildId === 'string') {
              parentMap[slotChildId] = parentNode;
            }
          });
        }
      }
    }

    if (parentNode.children && parentNode.children.length) {
      _.each(parentNode.children, function (childGuid) {
        parentMap[childGuid] = parentNode;
      });
    }
  });

  return parentMap;
};

/**
 * Given a leaf node, walks up the DOM tree to find an ancestor node that passes the matcher function.
 * @param  parentIndex A <nodeId>: <parentDomNode> index to look up node parents.
 * @param  leafNode    A JSON leaf node's data.
 * @param  matcher     The function to call for each ancestor node.
 * @return             A matching JSON node's data.
 */
export const findAncestorNodeBySelector = function (
  parentIndex: Record<string, JSONNode>,
  leafNode: JSONNode | null,
  matcher: (node: JSONNode) => void | boolean
): JSONNode | null {
  if (!leafNode) {
    return null;
  }

  const parentNode = parentIndex[leafNode._id];
  if (!parentNode) {
    return null;
  }

  if (matcher(parentNode)) {
    return parentNode;
  }

  // Keep walking up the DOM tree until there we hit a root node
  return findAncestorNodeBySelector(parentIndex, parentNode, matcher);
};
